/* Verbatim port of renderAI from service/review/index.html (~2435-2589) —
 * §3 AI narrative (AIQ reputation-prompt cards, per-engine model grid,
 * cited-domains bars, peer comparison, discoveries).
 *
 * PARAMETERIZED (rule a): reads the global `STATE` (STATE.aiNarrative,
 * STATE.brand, STATE.serp/STATE.serps, STATE.assets, STATE.peers) — takes
 * `state: AppPayloadState` as an explicit first parameter.
 *
 * PARAMETERIZED: `modelIcon` (lib/icons.ts) takes `apiBase` as an explicit
 * trailing parameter (F1) in place of the original's global `API_BASE` —
 * threaded through from api/client.ts's `apiBase` constant, same as every
 * other model-icon call site in this migration.
 *
 * F3 (wave B): innerHTML → lit render(); sub-render granularity preserved
 * (peerBars/peerRead get their own render() calls, same two elements the
 * original's `pEl.innerHTML =`/`rEl.innerHTML =` targeted separately).
 * `chip()` → `chipT()`; `tip()` → `tipT()`.
 *
 * SELF-CORRECTION (avoiding unsafeHTML, same call F3a made for entity.ts's
 * kpWord/chainClause): the original called `modelBadge(m.engine,22,
 * apiBase)` (lib/icons.ts), which returns a pre-built `<img>` HTML STRING —
 * this file now calls `modelIcon()` (same module, returns a plain URL
 * string, not markup) and builds the `<img>` tag itself as a native
 * TemplateResult, so no unsafeHTML is needed for the model badges.
 * `modelBadge` had no other consumer in the codebase (grepped) — it was
 * left in lib/icons.ts as dead code at the time, for symmetry with lib/
 * format.ts's chip()/tip() (wave C's territory). F4 item 3 later deleted
 * it outright once wave C confirmed it was genuinely unreachable.
 */
import { aiRoot, setAiRoot } from './mountRoot.ts';
import { html, nothing, render } from 'lit';
import { aiHeadline } from '../derive/ai.ts';
import { peerRank } from '../derive/search.ts';
import { assetForUrl } from '../derive/exec.ts';
import { CAT_COLOR, CAT_LABEL } from '../derive/axes.ts';
import { apiBase } from '../api/client.ts';
import { domainOf, favForDomain, fixMojibake, pct } from '../lib/format.ts';
import { tipT } from '../lib/templates.ts';
import { modelIcon } from '../lib/icons.ts';
import { openDrawer } from '../ui/drawer.ts';
import { chartCaption, chartEmpty, chartTableTwin, emphasisBarsSvg, emphasisLayout } from '../ui/charts.ts';
import { chartExportBar } from '../ui/chartExport.ts';
import {
  type PlaneProvenance,
  planePillClass,
  planeProvenanceLine,
  planeState,
} from '../derive/planes.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { AiCitedDomain, SerpCapture } from '../types/contract.ts';

export function renderAI(state: AppPayloadState, target?: HTMLElement): void {
  const el = target ?? document.getElementById('aiBody')!;
  setAiRoot(el);
  const ai = state.aiNarrative;
  if (!ai){
    // Honest empty-state (H1): render the AIQ plane's stamped provenance —
    // neutral for a deliberately `skipped` plane, amber-with-reason for an
    // `unavailable`/`error` (dark) plane — never the old "AIQ joins in at the
    // cross-referencing stage" coming-soon promise. Legacy runs (no
    // planeStatus) fall back to a neutral, promise-free line.
    const prov = planeProvenanceLine('aiq', planeState(state.planeStatus, 'aiq'));
    render(
      prov
        ? html`<div class="why"><span class="${planePillClass(prov.tone)}">${prov.text}</span></div>`
        : html`<div class="why">No AI-narrative data in this run.</div>`,
      el,
    );
    return;
  }
  const ah = aiHeadline(state);
  if (!ah) return; // unreachable — aiHeadline is null only when !ai (guarded above)
  const pr = peerRank(state);
  const peerWin = state.peers?.window;
  const doms = ai.citedDomains || [];
  const maxCount = Math.max(...doms.map((d) => d.count), 1);
  // Number(boolean) is the exact coercion the untyped `b.negative - a.negative` did
  const kps = (ai.keyPoints || []).slice().sort((a,b) => (Number(b.negative) - Number(a.negative)) || (b.engineCount - a.engineCount));
  const promoted = ai.promotedToAssets || [];
  const promotedDoms = promoted.map((p) => doms.find((d) => d.domain === p)).filter((d): d is AiCitedDomain => !!d);
  // domains on the branded Google search (§1) — to cross-flag AIQ drivers that also rank
  const serpDomains = new Set<string>();
  [state.serp, ...(state.serps||[])].filter((s): s is SerpCapture => !!s).forEach((s) => (s.results||[]).forEach((r) => { const d = r && r.url && domainOf(r.url); if (d) serpDomains.add(d); }));

  // "Model by model" subhead (FIX-audit F1b): the page renders engine CARRIAGE
  // (which engines carry which key points/themes), NOT per-engine sentiment —
  // AIQ's per-engine leans are a lean DERIVED from key-point carriage, not a
  // measured sentiment (service/tools/aiq.py: "source_models → per-engine
  // leans"), and the deep-AI page disclaims per-engine sentiment. So the subhead
  // is a carriage prompt, never a "the engines read it negative" sentiment claim.
  const modelAgreeCopy = 'compare what each engine carries side by side';

  // HONESTY (coverage-honesty audit P2-1): source ownership may be unmeasured
  // in this AIQ poll (aiHeadline preserves null rather than collapsing to 0).
  // When it is, we never render a "0% owned" figure or a 0% meter as if
  // measured — we say "not measured this run", mirroring the diff surface's
  // "not comparable" honesty. Peer self-citation (`pr`) is a SEPARATE AIQ
  // measurement and still shows when present.
  const ownMeasured = ah.ownership != null;
  const ownClause = ownMeasured
    ? html`only <b>${pct(ah.ownership)}%</b> of the ${ah.total.toLocaleString()} citations they draw on come from sources the client controls`
    : html`<b>source ownership wasn't measured this run</b> — this AIQ poll carries no owned-vs-earned split for the ${ah.total.toLocaleString()} citations they draw on`;
  const finding = html`Ask ${ah.engines} AI engines about ${state.brand} and they answer with <b>${ah.negThemes} negative theme${ah.negThemes!==1?'s':''}</b>${ah.unanimousNeg ? ` (${ah.unanimousNeg} repeated by every engine)` : ''} — and ${ownClause}${pr ? html`; counting own-domain citations alone, ${state.brand} ranks ${pr.rank === pr.of ? 'last' : '#'+pr.rank} in its ${pr.of}-brand tracked set at ${pct(pr.client.selfCitationShare)}%` : nothing}.`;

  // FIX-audit F1a: no fabricated engine roster. The reputation prompts' engine
  // count is a REAL list length (0 when the run didn't record it) — never a
  // `|| 8` default. When it's unknown we don't print a made-up "8 engines".
  const promptEngines = (ai.reputationPrompts?.[0]?.engines || []).length; // 0 = not recorded
  const basisCaption = (ai.reputationPrompts && ai.reputationPrompts.length) ? (
    (!promptEngines || promptEngines === ah.engines)
      // same (or unrecorded) basis: stats + cards both aggregate the reputation questions
      ? html`<div class="caption" style="margin:-6px 0 16px">All figures in this section aggregate the ${ai.reputationPrompts.length} purpose-built reputation questions AIQ tracks for ${state.brand} across ${ah.engines} engines${ai.window ? ` · ${ai.window.start} → ${ai.window.end}` : ''}.</div>`
      // recorded split basis (e.g. older runs): general question vs reputation prompts
      : html`<div class="caption" style="margin:-6px 0 16px">Two AIQ measurements feed this section: the citation figures above track the general "tell me about ${state.brand}" question across ${ah.engines} engines; the reputation questions below are tracked across ${promptEngines} engines.</div>`
  ) : nothing;

  const qcards = (ai.reputationPrompts && ai.reputationPrompts.length) ? ai.reputationPrompts.map((r) => {
    const s=(r.sentiment||'').toLowerCase();
    // Verdict + card accent from the aggregate lean (per-QUESTION, not per-engine).
    const v = s==='negative' ? {c:'v-bad', t:'Exposed', card:'q-neg', fix:'counter →',    bar:'var(--imp-neg)'}
            : s==='positive' ? {c:'v-ok',  t:'Clean',   card:'q-ok',  fix:'tracked §7 →', bar:'var(--imp-pos)'}
            : s==='mixed'    ? {c:'v-warn',t:'Mixed',   card:'q-mix', fix:'tracked §7 →', bar:'var(--amber)'}
            :                  {c:'v-warn',t:'Neutral', card:'q-mix', fix:'tracked §7 →', bar:'var(--imp-soso)'};
    // FIX-audit F1a/F1b: engine CARRIAGE only. Cells show which engines carry
    // this prompt (answered vs not) — NEVER coloured by per-engine lean/sentiment
    // (the deep-AI page disclaims per-engine sentiment; AIQ's per-engine lean is a
    // derived-from-carriage figure, not a measured sentiment). The engine total is
    // a REAL roster length — 0 (unknown) never becomes a fabricated 8.
    const engs=(r.engines||[]); const total=engs.length; // 0 when the roster isn't recorded
    const pe = r.perEngine && r.perEngine.length ? r.perEngine : null;
    // per-engine carriage cells: from perEngine (answered = carried an answer) or the
    // recorded engine roster (each listed engine carries the prompt). No lean colour.
    const cells: Array<{ engine: string; answered: boolean }> = pe
      ? pe.map((x) => ({ engine: x.engine, answered: x.lean !== 'not-answered' }))
      : engs.map((e) => ({ engine: String(e), answered: true }));
    const answered = pe
      ? pe.filter((x) => x.lean !== 'not-answered').length
      : (typeof r.engineCount === 'number' ? r.engineCount : (total || null));
    const bar = cells.map((c) => html`<i title="${c.engine} — ${c.answered ? 'carried an answer' : 'no answer carried'}"
      style="${c.answered ? 'background:var(--imp-soso-bg);box-shadow:inset 0 0 0 1px var(--imp-soso)' : 'background:var(--paper3)'}"></i>`);
    const clabel = (answered != null && total)
      ? `${answered} of ${total} engines carried an answer`
      : total
        ? `${total} engines tracked`
        : 'engine coverage not recorded';
    return html`<div class="qcard ${v.card}">
      <div class="qtop"><span class="qq">${r.question||r.label||''}</span>${r.sentiment?html`<span class="qverdict ${v.c}">${v.t}</span>`:nothing}</div>
      <div class="qcons"><span class="qbar">${bar}</span><span class="qclabel">${clabel}</span></div>
      <div class="qans"><span class="qal">What engines answer</span>${r.answer||''}</div>
      ${r.peerNote?html`<div class="qpeer">${r.peerNote}</div>`:nothing}
      <div class="qdrivers">${(r.drivers||[]).length ? html`<span class="dl">sources engines cite</span>${(r.drivers||[]).map((dr) => {
        const dn = domainOf('https://'+dr.domain) || dr.domain;
        const a = assetForUrl(state, 'https://'+dr.domain), cat = a && a.category;
        const catLabel = cat ? CAT_LABEL[cat] : 'not in the asset map';
        const inSerp = serpDomains.has(dn);
        return html`<a class="qsrc" href="${dr.topUrl||('https://'+dr.domain)}" target="_blank" rel="noopener" title="${dr.domain} — ${dr.count} citations · ${catLabel}${inSerp?' · also on branded search':''} · opens the cited page"><i class="qdot" style="background:${cat?CAT_COLOR[cat]:'var(--line)'}"></i>${dr.domain ? html`<img src="${favForDomain(dr.domain)}" alt="">` : nothing}${dr.domain}${inSerp?html`<span class="qserp" title="also ranks on the branded Google search">⌕</span>`:nothing}</a>`;
      })}` : nothing}<a class="qfix" href="#s7" title="take this to the §7 action plan">${v.fix}</a></div>
    </div>`; }) : nothing;

  const discoveries = promotedDoms.length ? promotedDoms.map((d) => html`<div class="discovery">
    <h4><img class="favi" src="${favForDomain(d.domain)}" alt="">${d.domain} — ${d.count} citations across ${d.models||'?'} engines</h4>
    <p>${d.count > (d.compareCount??d.count) ? `Up ${d.count-(d.compareCount??d.count)} vs last month. ` : ''}${d.label === 'Content mill' || /matrixbcg|portersfiveforce/.test(d.domain) ? 'A low-authority SEO site publishing templated analysis content — engines are citing it anyway. Publishing authoritative owned content on the same topics displaces it.' : /reuters|globes/.test(d.domain) ? 'Earned media the client cannot edit — but can pitch, and can outrank with an owned newsroom on the same stories.' : (d.note ? fixMojibake(d.note) : 'Assess and decide the play in §7.')}</p>
  </div>`) : nothing;

  const kpItems = kps.slice(0,8).map((k) => html`
    <div class="kpitem ${k.negative ? 'neg' : ''}">
      <h4>${k.negative ? '⚠ ' : ''}${k.title}</h4>
      <p>${k.description || ''}</p>
      <div class="eng">${k.engineCount} of ${ah.engines} engines${k.engineCount >= ah.engines ? ' — unanimous' : ''}${k.daysAppeared ? ` · seen ${k.daysAppeared}/30 days` : ''}</div>
    </div>`);

  const domBars = doms.slice(0,14).map((d) => {
    const mapped = (d.mappedAssetIds||[]).map((id) => state.assets.find((a) => a.id === id)).filter((a): a is NonNullable<typeof a> => !!a);
    const counts: Record<string, number> = {}; mapped.forEach((a) => counts[a.category] = (counts[a.category]||0)+1);
    const cat = Object.keys(counts).sort((a,b) => (counts[b]||0)-(counts[a]||0))[0] || null;
    const firstAsset = mapped.find((a) => a.category === cat) || mapped[0];
    const delta = d.count - (d.compareCount ?? d.count);
    return html`<div class="dombar" data-id="${firstAsset ? firstAsset.id : nothing}" style="${firstAsset ? 'cursor:pointer' : nothing}" @click=${() => firstAsset && openDrawer(state, firstAsset.id)}>
      <img src="${favForDomain(d.domain)}" alt="">
      <div class="dm"><div class="d">${d.domain}</div><div class="s">${cat ? CAT_LABEL[cat] : 'not in the asset map'}${d.note ? ' · ' + fixMojibake(d.note) : ''}</div></div>
      <div class="track"><div class="fill" style="width:${Math.round(d.count/maxCount*100)}%;background:${cat ? CAT_COLOR[cat] : 'var(--neg)'}"></div></div>
      <div class="val"><b>${d.count}</b> · ${delta>0?'▲+'+delta:delta<0?'▼'+Math.abs(delta):'flat'}</div>
    </div>`;
  });

  // FIX-audit F1b: engine CARRIAGE only — the key points + themes each engine
  // carries. The per-engine sentiment CHIP is gone (it framed AIQ's derived
  // lean as a measured per-engine sentiment, contradicting the page disclaimer).
  const modelGrid = (ai.perModel && ai.perModel.length) ? ai.perModel.map((m) => {
    const iconUrl = modelIcon(m.engine, apiBase);
    return html`<div class="modelcard">
      <div class="mh">${iconUrl ? html`<img class="micon" src="${iconUrl}" alt="${m.engine||''}" title="${m.engine||''}" style="width:22px;height:22px">` : nothing}<span class="me">${m.engine||''}</span></div>
      ${(m.keyPoints||[]).length ? html`<ul class="mkp">${(m.keyPoints||[]).slice(0,5).map((k) => html`<li>${typeof k==='string'?k:(k.title||'')}</li>`)}</ul>` : html`<div class="mnone">no distinct points captured</div>`}
      ${(m.topThemes||[]).length ? html`<div class="mth">${(m.topThemes||[]).slice(0,5).map((t) => html`<span>${t}</span>`)}</div>` : nothing}
    </div>`; }) : nothing;

  // §3 header meta lines (H1 fixes), reading stamped-but-previously-unrendered
  // fields. (a) D9 partial-content: if the AIQ plane ERRORED but still folded
  // some data (so we're on the content path, not the empty state), show the
  // amber "poll failed … data may be partial" line ABOVE the content, never
  // suppressing it. (b) Grammar-rider staleness: "AIQ narrative from cached
  // poll · N days old" — amber past the D7 threshold (aiNarrative.stale),
  // neutral otherwise — from aiNarrative.cacheAgeDays.
  const headerMeta: PlaneProvenance[] = [];
  const aiqPs = planeState(state.planeStatus, 'aiq');
  if (aiqPs && aiqPs.status === 'error') {
    const err = planeProvenanceLine('aiq', aiqPs);
    if (err) headerMeta.push(err);
  }
  const cacheAge = typeof ai.cacheAgeDays === 'number' ? ai.cacheAgeDays : null;
  if (cacheAge != null) {
    headerMeta.push({
      tone: ai.stale ? 'warn' : 'meta',
      text: `AIQ narrative from cached poll · ${cacheAge} day${cacheAge === 1 ? '' : 's'} old`,
    });
  }

  render(html`
    <div class="finding">${finding}</div>
    ${headerMeta.length ? html`<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px">${headerMeta.map((m) => html`<span class="${planePillClass(m.tone)}">${m.text}</span>`)}</div>` : nothing}
    <div class="why"><b>Why this matters:</b> AI answers are becoming the first impression — ChatGPT, Gemini and Google's AI Overviews now answer brand questions directly, citing their sources. Whoever those engines cite writes the client's story. ${tipT('source ownership','Source ownership')} measures how much of that story the client feeds; the fix is getting owned, authoritative content into what engines cite.</div>

    <div class="stats">
      ${ownMeasured
        ? html`<div class="stat"><div class="n">${pct(ah.ownership)}<small>%</small></div><div class="l">of AI citations come from client-controlled sources (own sites + profiles it runs)${pr ? ` · own domains alone: ${pct(pr.client.selfCitationShare)}%` : ''}</div><div class="meter"><i style="width:${pct(ah.ownership)}%"></i></div></div>`
        : html`<div class="stat"><div class="n" style="color:var(--muted)">—</div><div class="l">source ownership <b>not measured this run</b> — this AIQ poll has no owned-vs-earned citation split${pr ? ` · own domains alone: ${pct(pr.client.selfCitationShare)}%` : ''}</div></div>`}
      <div class="stat"><div class="n">${ah.total.toLocaleString()}</div><div class="l">citations tracked this month, across ${ah.engines} engines</div></div>
      <div class="stat ${ah.unanimousNeg?'bad':''}"><div class="n">${ah.unanimousNeg}</div><div class="l">negative themes repeated by every engine</div></div>
      <div class="stat ${promoted.length?'warn':''}"><div class="n">${promoted.length}</div><div class="l">AI-cited sources nobody had on the radar (found this run)</div></div>
    </div>
    ${basisCaption}

    ${(ai.reputationPrompts && ai.reputationPrompts.length) ? html`<div class="card" style="margin-bottom:16px;border-top:3px solid var(--neg)">
      <h3>The questions people actually ask — and what every AI engine answers</h3>
      <div class="sub">purpose-built reputation prompts run across ${promptEngines ? `${promptEngines} tracked engines` : 'the tracked engines'} — this is the answer a prospect, member, or regulator sees · AIQ</div>
      <div class="qgrid">${qcards}</div>
    </div>` : nothing}

    ${promotedDoms.length ? html`<div class="card" style="margin-bottom:16px">
      <h3>Discoveries — AI-narrative drivers that weren't in the mapped footprint</h3>
      <div class="sub">cited heavily enough by AI engines that this run added them to the asset map automatically — they now need an owner and a play</div>
      ${discoveries}</div>` : nothing}

    <div class="cardrow cols-2">
      <div class="card">
        <h3>What the engines are saying</h3>
        <div class="sub">recurring themes across ${ah.engines} engines this month — negative first · from AIQ</div>
        <div id="kpList">${kpItems}</div>
        ${kps.length > 8 ? html`<div class="caption">${kps.length - 8} further themes in the appendix data.</div>` : nothing}
      </div>
      <div>
        <div class="card" style="margin-bottom:16px">
          <h3>Who feeds the engines</h3>
          <div class="sub">domains AI engines cite about ${state.brand} — bar = citations this month, color = how much the client controls the source</div>
          <div id="domBars">${domBars}</div>
        </div>
        <div class="card">
          <h3>How peers compare</h3>
          <div class="sub">${tipT('self-citation share','self-citation share')} — how much of each tracked brand's AI narrative comes from its own domains · ${peerWin ? peerWin.start+' → '+peerWin.end : ''} · AIQ</div>
          <div id="peerBars"></div>
          <div id="peerRead"></div>
        </div>
      </div>
    </div>

    ${(ai.perModel && ai.perModel.length) ? html`<div class="card" style="margin-top:16px">
      <h3>Model by model — how each AI engine answers</h3>
      <div class="sub">the same branded question, asked of ${ai.perModel.length} engines — ${modelAgreeCopy} · AIQ</div>
      <div class="modelgrid">${modelGrid}</div>
    </div>` : nothing}`, el);

  // peers — CH-4 emphasis bars (chart-pack §1 CH-4): client azure, peers gray,
  // sorted desc, value % outside each bar, mandatory tracked-set caption, aria
  // takeaway, table-view twin. The rank chip and the peerRead verdict stay.
  if (pr){
    const layout = emphasisLayout(
      pr.sorted.map((x) => ({ label: x.name, value: x.selfCitationShare, highlight: x.isClient })),
    );
    const cap = `Self-citation share across the ${pr.of}-brand tracked set · AIQ${peerWin ? ` · ${peerWin.start} → ${peerWin.end}` : ''}`;
    const aria = `Self-citation share across ${pr.of} tracked brands: ${pr.client.name} ranks number ${pr.rank} of ${pr.of} at ${pct(pr.client.selfCitationShare)} percent.`;
    const peerCols = ['Brand', 'Self-citation share'];
    const peerRows = pr.sorted.map((x) => [x.name + (x.isClient ? ' (client)' : ''), `${pct(x.selfCitationShare)}%`]);
    render(html`
      <div class="chartwrap">
        <div class="chart-head">
          <span class="pill-state acc pill-sm" title="${pr.client.name} ranks #${pr.rank} of the ${pr.of}-brand tracked set on AI self-citation share">#${pr.rank} of ${pr.of}</span>
        </div>
        <div class="chart-scroll">${emphasisBarsSvg(layout, aria)}</div>
        ${chartCaption(cap)}
        ${chartExportBar({ title: 'AI self-citation vs peers', caption: cap, tableCols: peerCols, tableRows: peerRows })}
        ${chartTableTwin(peerCols, peerRows)}
      </div>`, aiRoot().querySelector<HTMLElement>('#peerBars')!);
    const gap = pct(pr.best.selfCitationShare) - pct(pr.client.selfCitationShare);
    render(pr.client.name === pr.best.name
      ? html`<p style="font-size:13px;color:var(--ink-2);margin-top:10px"><b>${pr.client.name} leads its peer set</b> on AI-narrative control.</p>`
      : html`<p style="font-size:13px;color:var(--ink-2);margin-top:10px;line-height:1.55"><b>${pr.client.name}'s AI story is written by third parties.</b> Peers' own corporate and IR content dominates what engines cite about them; ${pr.client.name} trails ${pr.best.name} by ${gap} points, with ${pr.client.topThirdPartySource || 'third-party sites'} as its loudest third-party voice. Closing the gap means getting more owned content into what the engines cite.</p>`, aiRoot().querySelector<HTMLElement>('#peerRead')!);
  } else {
    render(chartEmpty('No peer set configured in AIQ for this run.'), aiRoot().querySelector<HTMLElement>('#peerBars')!);
    render(html``, aiRoot().querySelector<HTMLElement>('#peerRead')!);
  }
}
